import { type RefObject, useEffect, useRef } from 'react';

export default function useIframeSizeObserver(
  ref: RefObject<HTMLIFrameElement>,
  callback: (params: { height: number; width: number }) => void,
) {
  const observerRef = useRef<null | ResizeObserver>(null);

  useEffect(() => {
    const iframe = ref.current;

    iframe?.addEventListener('load', handleLoad);

    return () => {
      iframe?.removeEventListener('load', handleLoad);
      observerRef.current?.disconnect();
    };

    function handleLoad() {
      const iframeDoc = getIframeDocument();
      if (iframeDoc) {
        callCallback();
        observerRef.current = new ResizeObserver(() => callCallback());
        observerRef.current.observe(iframeDoc.body);
      }
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  function getIframeDocument(): Document | undefined {
    try {
      return (
        ref.current?.contentDocument || ref.current?.contentWindow?.document
      );
    } catch {
      return undefined;
    }
  }

  function callCallback() {
    const iframeDoc = getIframeDocument();
    if (iframeDoc) {
      const { scrollHeight } = iframeDoc.body;
      const { scrollWidth } = iframeDoc.body;
      callback({ width: scrollWidth, height: scrollHeight });
    }
  }
}
